import React, { useState, useContext, useEffect } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, btnP, btnSec, rad, sp, icBox } from "../../constants/styles";
import Avatar from "../common/Avatar";
import { can } from "../../utils/permissions";
import Ic from "../common/Ic";
import { isExpiringSoon, getDaysRemaining, getLeaseForProperty, isOverdue } from "../../utils/leaseHelpers";
import { formatPropAddr } from "../../utils/propertyAddr";
import { getPlanUsage } from "../../utils/plans";
import UpgradeModal from "../common/UpgradeModal";
import { IS_DESKTOP } from "../../constants/platform";
import useDragScroll from "../../utils/dragScroll";
import { isCommercial, ptLabel } from "../../utils/propertyTypes";

function LandlordDash() {
  const { t, h, props, setProps, tens, setTens, pays, setPays, mts, go, setSelPropId, flash, addNotif, showConfirm, leases, setLeases, role, plan, refreshData, userName, orgName, orgRole, setOpenUpgradeOnSettings } = useContext(Ctx);
  const usage = getPlanUsage(plan.type, props.length, plan.maxProperties);
  const planFeatures = plan.features || [];
  const isFree = plan.type === "free";
  // Map quick action pages to required features for lock badge
  const quickActionFeatures = {
    agreement: "leases",
    servicePersons: "service_persons",
    reports: "reports",
  };
  const isFeatureLocked = (pg) => {
    const feat = quickActionFeatures[pg];
    return feat && !planFeatures.includes(feat);
  };
  const [newLeadCount, setNewLeadCount] = useState(0);
  const [hotLeadCount, setHotLeadCount] = useState(0);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [todayVisits, setTodayVisits] = useState([]);
  const [upcomingVisits, setUpcomingVisits] = useState([]);
  const [followUpCount, setFollowUpCount] = useState(0);
  // Outstanding total — must match the "Due" total shown on PayPage. The
  // old formula filtered only `st === "due"` and summed gross, which
  // (a) missed partial-payment rows and (b) double-counted amounts the
  // tenant had already paid in approved txns. Result: Dashboard's
  // "Outstanding" silently disagreed with PayPage's "Due" by exactly the
  // partial-payment delta, confusing landlords. Use the same formula
  // as PayPage so both numbers always agree.
  const due = pays
    .filter(p => (p.st === "due" || p.st === "partial") && !p.pending)
    .reduce((s, p) => {
      const gross = (p.amt || 0) + (p.com || 0);
      const txnPaid = (p.txns || []).reduce((sum, tx) => sum + (tx.status === "verified" ? tx.amt : 0), 0);
      return s + Math.max(0, gross - txnPaid);
    }, 0);
  // Total received — mirrors PayPage's "Total Received": every verified txn.
  const received = pays.reduce((s, p) => s + (p.txns || []).reduce((x, tx) => x + (tx.status === "verified" ? tx.amt : 0), 0), 0);
  const pendingPays = pays.filter(p => p.pending);
  const partialPays = pays.filter(p => p.st === "partial");
  const recentRzpPays = pays.filter(p => {
    const txns = p.txns || [];
    const today = new Date().toISOString().slice(0, 10);
    return txns.some(tx => tx.mth === "razorpay" && tx.status === "verified" && tx.dt === today);
  });
  const [showAllActions, setShowAllActions] = useState(false);
  // Mouse users can't swipe a scrollbar-less row — these add click-drag
  // panning + vertical-wheel → horizontal scroll on the card strips.
  const propDrag = useDragScroll();
  const tenDrag = useDragScroll();
  const overduePays = pays.filter(p => (p.st === "due" || p.st === "partial") && !p.pending && isOverdue(p.mo));
  const overdueTotal = overduePays.reduce((s, p) => s + p.amt + (p.com || 0), 0);
  const openMaintItems = mts.filter(m => m.st === "open" || m.st === "assigned");

  useEffect(() => {
    const autoGen = async () => {
      const res = await api.generateDuePayments();
      if (res.success && res.generated > 0) {
        flash(`${t.generateMsg} ${res.generated} ${t.newRentBills} ${res.month}`);
        const data = await api.sync();
        if (data && data.pays) setPays(data.pays);
      }
    };
    autoGen();
  }, []);

  // Phase 16: Fetch new lead count + Phase 18: visit data
  useEffect(() => {
    (async () => {
      const data = await api.getLeads();
      if (data.success) {
        const leads = data.leads;
        setNewLeadCount(leads.filter(l => l.status === "new").length);
        setHotLeadCount(leads.filter(l => {
          if (l.status !== "new") return false;
          const hrs = (Date.now() - new Date(l.created_at).getTime()) / (1000 * 60 * 60);
          const hasSignal = (l.message && l.message.trim()) || l.pref_rooms || l.pref_max_rent;
          return hrs < 72 || hasSignal;
        }).length);
        // Phase 18: Visit tracking
        const today = new Date().toISOString().split('T')[0];
        setTodayVisits(leads.filter(l => l.visit_date === today && l.visit_status === "scheduled"));
        setUpcomingVisits(leads.filter(l => l.visit_date > today && l.visit_status === "scheduled"));
        // Phase 19: Follow-up alerts
        setFollowUpCount(leads.filter(l => l.follow_up_sent === 1 && l.status === "new").length);
      }
    })();
  }, []);

  const expiringLeases = leases.filter(l => isExpiringSoon(l));
  const vacReqs = tens.filter(t => t.vac);

  // New-flow vacate requests live in their own table (vacate_requests)
  // and aren't part of the main sync payload. We fetch them once on
  // mount so the dashboard's Needs Attention section surfaces them
  // — without this, a tenant submitting through the new VacatePage
  // wouldn't show up here at all (their `t.vac` flag stays false).
  //
  // Statuses surfaced:
  //   under_review        — tenant just submitted, awaiting review
  //   deduction_proposed  — landlord proposed, awaiting tenant accept
  //   full_refund_proposed— landlord proposed, awaiting tenant accept
  //   agreed              — tenant accepted, awaiting LANDLORD sign + settle
  //   disputed            — tenant rejected, frozen pending admin
  //
  // 'initiated' is in-progress on the tenant side (still uploading);
  // 'settled' / 'closed' / 'cancelled' are terminal.
  const [newVacReqs, setNewVacReqs] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.vacateListOrg().then(res => {
      if (cancelled || !res?.success) return;
      const open = (res.vacate_requests || [])
        .filter(v => ["under_review", "deduction_proposed", "full_refund_proposed", "agreed", "disputed"].includes(v.status));
      setNewVacReqs(open);
    }).catch(() => { /* silent — banner just stays hidden */ });
    return () => { cancelled = true; };
  }, []);

  const attentionCount = overduePays.length + pendingPays.length + partialPays.length + recentRzpPays.length + expiringLeases.length + openMaintItems.length + vacReqs.length + newVacReqs.length + newLeadCount + followUpCount;
  const approveVacate = (te) => {
    showConfirm(`${t.approveVacFor} ${te.name}?`, () => {
      api.approveVacate(te.id, te.pid);
      setTens(tens.map(t => t.id === te.id ? { ...t, vac: false, pid: null } : t));
      setProps(props.map(p => p.id === te.pid ? { ...p, occ: false, tid: null } : p));
      setLeases(leases.map(l => l.tenant_id === te.id && (l.status === "active" || l.status === "draft") ? { ...l, status: "terminated" } : l));
      addNotif("landlord", null, `Approved vacate for ${te.name}`, "info");
      addNotif("tenant", te.id, t.vacApprovedMsg, "info");
      flash(t.vacApproved);
    });
  };
  const rejectVacate = (te) => {
    showConfirm(`${t.rejectVacFor} ${te.name}?`, () => {
      api.rejectVacate(te.id);
      setTens(tens.map(t => t.id === te.id ? { ...t, vac: false } : t));
      addNotif("landlord", null, `Rejected vacate for ${te.name}`, "info");
      addNotif("tenant", te.id, t.vacRejectedMsg, "info");
      flash(t.vacRejected);
    });
  };

  // Attention item renderer
  const AttItem = ({ icon, color, title, sub, onClick, last }) => (
    <div onClick={onClick} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${sp.md}px ${sp.lg}px`, borderBottom: last ? "none" : `1px solid ${h.bd}`, cursor: "pointer", transition: "background 0.15s" }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.md }}>
        <div style={icBox(color, 36)}><span style={{ fontSize: 15 }}>{icon}</span></div>
        <div><div style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{title}</div>
          <div style={{ color: h.ts || h.sc, fontSize: 11, marginTop: 1 }}>{sub}</div></div>
      </div>
      <Ic n="back" s={14} c={h.tt || h.sc} style={{ transform: "rotate(180deg)" }} />
    </div>
  );

  // Plan-expired banner state. Sits right under the welcome heading
  // because every other gate already triggers off plan_status. Showing
  // it as a red call-out (rather than relying on the small Settings
  // pill) makes the renewal CTA impossible to miss.
  const planExpired = plan?.planStatus === 'expired';
  const planLabel = plan?.name || (plan?.type ? plan.type.charAt(0).toUpperCase() + plan.type.slice(1) : '');
  // Compute remaining grace-period days. The auto-downgrade cron flips
  // the org to Free at day 7 post-expiry, so grace = 7 - daysSinceExpiry.
  // Mirror this server-side math here so the banner countdown matches
  // what the cron will actually do.
  const graceDaysRemaining = (() => {
    if (!planExpired || !plan?.planEnd) return null;
    if (plan?.type === 'free') return 0; // already auto-downgraded
    const ms = Date.now() - new Date(plan.planEnd).getTime();
    const daysSince = Math.floor(ms / (1000 * 60 * 60 * 24));
    // Use the super-admin-configurable grace length the server echoes
    // on each sync, falling back to 7 if older sync schema.
    const graceDays = typeof plan?.gracePeriodDays === 'number' ? plan.gracePeriodDays : 7;
    return Math.max(0, graceDays - daysSince);
  })();

  return <div style={{ paddingTop: sp.lg }} className="animate-fadeIn">
    {/* Welcome — time-of-day greeting over a large-title name, with a
        one-line subtitle (reference mock). */}
    <div style={{ marginBottom: sp.xl }}>
      <div style={{ color: h.ts, fontSize: 14, fontWeight: 500 }}>{(() => { const hr = new Date().getHours(); return hr < 12 ? (t.goodMorning || "Good morning") : hr < 17 ? (t.goodAfternoon || "Good afternoon") : (t.goodEvening || "Good evening"); })()},</div>
      <h2 style={{ color: h.tx, fontSize: 30, fontWeight: 700, margin: "2px 0 5px", letterSpacing: "-0.8px", lineHeight: 1.12 }}>{userName || orgName || t.landlord || "Landlord"}<span style={{ fontSize: 17, marginLeft: 7, verticalAlign: "3px", opacity: 0.9 }}>👋</span></h2>
      <div style={{ color: h.tt, fontSize: 12.5, fontWeight: 500 }}>{t.dashSubtitle || "Here's what's happening with your properties."}</div>
    </div>

    {/* Plan-expired alert — read-only grace period in effect:
        existing data still works, new actions on paid features are
        blocked server-side until the user renews. Tap deep-links to
        the Upgrade modal: sets the pending-open flag, then nav to
        settings; SettingsPage reads the flag on mount and pops the
        modal automatically. Saves the user one click vs. landing on
        Settings and hunting for Renew Plan. */}
    {planExpired && <div onClick={() => { setOpenUpgradeOnSettings(true); go("settings"); }} style={{
      background: `${h.rd}10`,
      border: `1px solid ${h.rd}40`,
      borderRadius: rad.lg,
      padding: sp.md + 2,
      marginBottom: sp.xl,
      display: "flex",
      alignItems: "center",
      gap: sp.md,
      cursor: "pointer",
      boxShadow: `0 2px 8px ${h.rd}15`,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: "50%",
        background: `${h.rd}20`,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, fontSize: 18,
      }}>{"⚠️"}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: h.rd, fontSize: 13, fontWeight: 700, letterSpacing: "-0.1px" }}>
          {planLabel ? `${planLabel} ${t.planExpired || "Plan Expired"}` : (t.planExpired || "Plan Expired")}
        </div>
        {graceDaysRemaining != null && <div style={{ color: h.rd, fontSize: 11, fontWeight: 700, marginTop: 2 }}>
          {graceDaysRemaining > 0
            ? `⏳ ${graceDaysRemaining} day${graceDaysRemaining === 1 ? '' : 's'} left to renew before downgrade to Free`
            : '⚠️ Auto-downgrade to Free in effect'}
        </div>}
        <div style={{ color: h.sc, fontSize: 11, marginTop: 2, lineHeight: 1.4 }}>
          {t.planExpiredHint || "New properties, agreements, and SP assignments are paused. Tap to renew."}
        </div>
      </div>
      <div style={{ color: h.rd, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
        {(t.renewPlan || "Renew")} {"›"}
      </div>
    </div>}

    {/* Overview — ONE glassmorphism container with hairline dividers
        between Received · Due · Tenants (user request). */}
    <div style={{ marginBottom: sp.xxl }}>
      <h3 style={{ color: h.tx, fontSize: 16, fontWeight: 600, margin: "0 0 10px", letterSpacing: "-0.2px" }}>{t.overview || "Overview"}</h3>
      <div style={{ display: "flex", alignItems: "stretch", borderRadius: 20, padding: "15px 4px", background: h.dk ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.62)", border: `1px solid ${h.dk ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.85)"}`, backdropFilter: "blur(20px) saturate(160%)", WebkitBackdropFilter: "blur(20px) saturate(160%)", boxShadow: h.sh }}>
        {[
          { v: `₹${received.toLocaleString()}`, l: t.received || "Received", c: h.gn, pg: "pay" },
          { v: `₹${due.toLocaleString()}`, l: t.due || "Due", c: h.a2, pg: "pay" },
          { v: tens.length, l: t.tenants, c: h.in, pg: "tens" },
        ].map((c, i) => (
          <div key={i} onClick={() => go(c.pg)} style={{ flex: 1, minWidth: 0, textAlign: "center", cursor: "pointer", borderLeft: i > 0 ? `1px solid ${h.bd}` : "none", padding: "2px 6px" }}>
            <div style={{ color: c.c, fontSize: 16.5, fontWeight: 700, letterSpacing: "-0.3px", fontVariantNumeric: "tabular-nums", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.v}</div>
            <div style={{ color: h.ts, fontSize: 11, fontWeight: 500, marginTop: 3 }}>{c.l}</div>
          </div>
        ))}
      </div>
    </div>

    {/* Needs Attention */}
    {attentionCount > 0 ? <div data-coach="dash-recent" style={{ marginBottom: sp.xxl }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.md }}>
        <h3 style={{ color: h.tx, fontSize: 15, fontWeight: 700, margin: 0, letterSpacing: "-0.1px" }}>{t.needsAttention}</h3>
        <span style={{ background: `${h.rd}12`, color: h.rd, padding: "2px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700 }}>{attentionCount}</span>
      </div>
      <div style={{ background: h.cd, borderRadius: 22, overflow: "hidden", boxShadow: h.sh }}>
        {overduePays.length > 0 && <AttItem icon="!!" color={h.rd} title={t.overdueRents} sub={`${overduePays.length} ${t.tenants.toLowerCase()} \u00B7 \u20B9${overdueTotal.toLocaleString()}`} onClick={() => go("pay")} />}
        {pendingPays.length > 0 && <AttItem icon={"\u23F3"} color={h.wn} title={t.pendingApprovals} sub={`${pendingPays.length} ${t.payments.toLowerCase()}`} onClick={() => go("pay")} />}
        {recentRzpPays.length > 0 && <AttItem icon="💳" color="#16a34a" title="New Razorpay Payment" sub={`${recentRzpPays.length} payment(s) received today`} onClick={() => go("pay")} />}
        {partialPays.length > 0 && <AttItem icon="🟡" color={h.wn} title="Partial Payments" sub={`${partialPays.length} tenant(s) paid partially`} onClick={() => go("pay")} />}
        {expiringLeases.length > 0 && <AttItem icon={"\uD83D\uDCCB"} color={h.wn} title={t.expiringLeases} sub={`${expiringLeases.length} \u2014 ${Math.min(...expiringLeases.map(l => getDaysRemaining(l)))} ${t.days}`} onClick={() => go("agreement")} />}
        {/* New-flow vacate requests \u2014 separate from legacy `vac` flag,
            so the landlord doesn't miss a freshly submitted move-out
            sitting in the inbox. Sub-count breaks out the four real
            states the landlord cares about, ordered by urgency:
              awaitingSig (tenant accepted) \u2014 landlord MUST sign
              awaitingReview (tenant submitted) \u2014 landlord reviews
              disputed (frozen pending admin)
              proposalSent (awaiting tenant accept) */}
        {newVacReqs.length > 0 && (() => {
          const awaitingReview = newVacReqs.filter(v => v.status === "under_review").length;
          const awaitingSig    = newVacReqs.filter(v => v.status === "agreed").length;
          const disputed       = newVacReqs.filter(v => v.status === "disputed").length;
          const proposalSent   = newVacReqs.length - awaitingReview - awaitingSig - disputed;

          const parts = [];
          if (awaitingSig > 0)    parts.push(`${awaitingSig} awaiting your sign`);
          if (awaitingReview > 0) parts.push(`${awaitingReview} new`);
          if (disputed > 0)       parts.push(`${disputed} disputed`);
          if (proposalSent > 0)   parts.push(`${proposalSent} awaiting tenant`);

          // Single-state messages get the punchier copy. Multi-state
          // joins with ' \u00B7 ' to keep it scannable on mobile.
          const sub = parts.length === 1
            ? (awaitingSig > 0    ? `${awaitingSig} awaiting your signature`
             : awaitingReview > 0 ? `${awaitingReview} new \u2014 review now`
             : disputed > 0       ? `${disputed} disputed \u2014 admin reviewing`
             : `${proposalSent} awaiting tenant response`)
            : parts.join(' \u00B7 ');

          // Icon priority: sign > dispute > generic. \u270D\uFE0F when the
          // landlord has signature work to do, \uD83D\uDEA9 if there's any
          // dispute, otherwise the door icon.
          const icon = awaitingSig > 0 ? "\u270D\uFE0F"
                     : disputed > 0     ? "\uD83D\uDEA9"
                     : "\uD83D\uDEAA";
          return <AttItem icon={icon} color={h.rd} title={t.vacateRequests || "Vacate Requests"} sub={sub} onClick={() => go("vacateInbox")} />;
        })()}
        {openMaintItems.length > 0 && (() => { const highCount = openMaintItems.filter(m => m.urg === "high").length; return <AttItem icon={"\uD83D\uDD27"} color={h.rd} title={t.openMaint} sub={`${highCount > 0 ? highCount + " " + t.high.toLowerCase() + " \u00B7 " : ""}${openMaintItems.length} ${t.total.toLowerCase()}`} onClick={() => go("maint")} />; })()}
        {vacReqs.length > 0 && <div style={{ padding: `${sp.md}px ${sp.lg}px` }}>
          <div style={{ display: "flex", alignItems: "center", gap: sp.md, marginBottom: sp.md }}>
            <div style={icBox(h.rd, 36)}><span style={{ fontSize: 15 }}>{"\uD83C\uDFE0"}</span></div>
            <div><div style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{t.vacateRequests}</div>
              <div style={{ color: h.ts || h.sc, fontSize: 11 }}>{vacReqs.length} {t.tenants.toLowerCase()}</div></div>
          </div>
          {vacReqs.map(te => {
            const p = props.find(x => x.id === te.pid);
            return <div key={te.id} style={{ background: h.sf || h.bg, borderRadius: rad.md, padding: sp.md, marginBottom: sp.xs + 2 }}>
              <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.sm }}>
                <Avatar src={te.avatar_url} name={te.name} size={28} bg={h.wn} fontSize={12} />
                <div><div style={{ color: h.tx, fontWeight: 600, fontSize: 12 }}>{te.name}</div><div style={{ color: h.ts || h.sc, fontSize: 10 }}>{p ? formatPropAddr(p) : t.noProperty}</div></div>
              </div>
              <div style={{ display: "flex", gap: sp.sm }}>
                <button onClick={() => approveVacate(te)} style={{ ...btnP(h), background: h.gn, flex: 1, fontSize: 12, padding: 10, boxShadow: `0 2px 8px ${h.gn}25` }}>{"\u2713 " + t.approve}</button>
                <button onClick={() => rejectVacate(te)} style={{ ...btnSec(h), color: h.rd, flex: 1, fontSize: 12, padding: 10 }}>{"\u2715 " + t.reject}</button>
              </div>
            </div>;
          })}
          {/* Bridge to the new state-machine vacate flow. Legacy
              approve/reject above stays for back-compat; the inbox
              opens the full photo-review + deduction composer. */}
          <button onClick={() => go("vacateInbox")} style={{
            width: "100%", marginTop: sp.sm, padding: 10,
            background: `${h.ac}10`, border: `1px solid ${h.ac}40`,
            borderRadius: rad.sm, color: h.ac, cursor: "pointer",
            fontSize: 12, fontWeight: 600,
          }}>{"\ud83d\udce6 Open Full Vacate Inbox \u2192"}</button>
        </div>}
        {newLeadCount > 0 && <AttItem icon={"\uD83D\uDCE9"} color={h.ac} title={`${hotLeadCount > 0 ? ("\uD83D\uDD25 " + hotLeadCount + " " + (t.hotLeads || "Hot") + " \u00B7 ") : ""}${newLeadCount} ${t.newLeads || "New Leads"}`} sub={t.tapToReview || "Tap to review"} onClick={() => go("leads")} />}
        {followUpCount > 0 && <AttItem icon={"\uD83D\uDD14"} color={h.wn} title={`${followUpCount} ${t.followUpNeeded || "Need Follow-up"}`} sub={t.followUpDesc || "Auto-reminded, still uncontacted"} onClick={() => go("leads")} last />}
      </div>
    </div> : null}
    {/* "All clear / nothing to see" card removed \u2014 when nothing needs
        attention we render nothing. Saves a full-width card of vertical
        space and follows the principle: notify only when there's
        something to notify about. The card returns automatically the
        moment any attentionCount item appears. */}

    {/* Phase 18: Today's Visits */}
    {todayVisits.length > 0 && <div style={{ marginBottom: sp.xl }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.md }}>
        <h3 style={{ color: h.tx, fontSize: 14, fontWeight: 700, margin: 0 }}>{t.todaysVisits || "Today's Visits"}</h3>
        <span style={{ background: `${h.ac}12`, color: h.ac, padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>{todayVisits.length}</span>
      </div>
      <div style={{ background: h.cd, borderRadius: rad.lg, overflow: "hidden", border: `1px solid ${h.bd}`, boxShadow: h.sh }}>
        {todayVisits.map((v, i) => <div key={v.id} onClick={() => go("leads")} style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${sp.md}px ${sp.lg}px`,
          borderBottom: i < todayVisits.length - 1 ? `1px solid ${h.bd}` : "none", cursor: "pointer"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: sp.md }}>
            <div style={{ ...icBox(h.ac, 36), fontWeight: 700, fontSize: 11, color: h.ac }}>{v.visit_time}</div>
            <div>
              <div style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{v.name}</div>
              <div style={{ color: h.ts || h.sc, fontSize: 11 }}>{v.property_addr || v.phone}</div>
            </div>
          </div>
          <Ic n="back" s={14} c={h.tt || h.sc} />
        </div>)}
      </div>
    </div>}

    {/* Phase 18: Upcoming Visits */}
    {upcomingVisits.length > 0 && <div style={{ marginBottom: sp.xl }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.sm }}>
        <span style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{t.upcomingVisits || "Upcoming Visits"}</span>
        <span style={{ color: h.ts || h.sc, fontSize: 11 }}>({upcomingVisits.length})</span>
      </div>
      <div style={{ background: h.cd, borderRadius: rad.md, padding: `${sp.sm}px ${sp.lg}px`, border: `1px solid ${h.bd}`, boxShadow: h.sh }}>
        {upcomingVisits.slice(0, 3).map(v => <div key={v.id} onClick={() => go("leads")} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", cursor: "pointer" }}>
          <span style={{ color: h.tx, fontSize: 12, fontWeight: 500 }}>{v.name}</span>
          <span style={{ color: h.ts || h.sc, fontSize: 11 }}>{new Date(v.visit_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} {"\u00B7"} {v.visit_time}</span>
        </div>)}
        {upcomingVisits.length > 3 && <div style={{ color: h.ac, fontSize: 11, marginTop: sp.xs, textAlign: "center", fontWeight: 500 }}>+{upcomingVisits.length - 3} {t.more || "more"}</div>}
      </div>
    </div>}

    {/* Metrics now live in the hero + quick-summary cards above
        (Revenue/Collected detail remains on the Analytics page). */}

    {/* Revenue Trend chart moved to /analytics (top-right gear → Analytics).
        Keeps the dashboard focused on actionable status (cash due,
        properties, agreements + the property grid below). */}

    {/* Plan usage strip — owner only */}
    {isFree && can(orgRole, "upgrade") && <div style={{ display: "flex", alignItems: "center", gap: sp.md, background: h.cd, borderRadius: rad.md, padding: `${sp.md}px ${sp.lg}px`, marginBottom: sp.xxl, border: `1px solid ${h.bd}`, boxShadow: h.sh }}>
      <div style={icBox(h.ac, 32)}><span style={{ fontSize: 13 }}>{"\uD83D\uDCE6"}</span></div>
      <div style={{ flex: 1 }}>
        <div style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>{t.planFreeLabel || "Free Plan"}: {usage.used}/{usage.limit} {t.properties.toLowerCase()}</div>
        <div style={{ background: h.ib, borderRadius: 3, height: 4, marginTop: 4, overflow: "hidden" }}>
          <div style={{ background: usage.atLimit ? h.wn : h.ac, height: "100%", width: `${usage.pct}%`, borderRadius: 3, transition: "width 0.5s ease" }} />
        </div>
      </div>
      <button onClick={() => setShowUpgrade(true)} style={{ background: h.pp || h.ac, color: "#fff", border: "none", borderRadius: rad.sm, padding: `${sp.xs + 2}px ${sp.lg - 2}px`, fontSize: 10, fontWeight: 600, cursor: "pointer", boxShadow: `0 2px 8px ${h.pp || h.ac}30`, whiteSpace: "nowrap" }}>{usage.atLimit ? (t.planUpgrade || "Upgrade") : (t.planUpgrade || "Upgrade")}</button>
    </div>}

    {/* Quick Actions \u2014 reference mock: one row of 4 coral-tinted
        creation tiles + "View all" revealing the rest. Same routes and
        plan-locks as always; press feedback via scale. */}
    <div data-coach="dash-quick-actions" style={{ marginBottom: sp.xxl }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <h3 style={{ color: h.tx, fontSize: 16, fontWeight: 600, margin: 0, letterSpacing: "-0.2px" }}>{t.quick}</h3>
        <span onClick={() => setShowAllActions(v => !v)} style={{ color: h.a2, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>{showAllActions ? (t.showLess || "Less") : (t.viewAll || "View all")}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
        {[
          [t.addProp || "Add Property", "home", "addProp"],
          [t.addTenant || "Add Tenant", "user", "addTenant"],
          [t.agreements, "file", "agreement"],
          [t.maint || "Maintenance", "wrench", "maint"],
          ...(showAllActions ? [
            [t.leads || "Leads", "users", "leads"],
            [t.invite, "send", "inviteTenant"],
            [t.mapView, "navigation", "mapView"],
            [t.whatsappSettings || "WhatsApp", "message-circle", "paySettings"],
            [t.servicePersons, "gear", "servicePersons"],
            [t.reports || "Reports", "grid", "reports"],
          ] : []),
        ].map(([l, ic, pg], i) => {
          const locked = isFeatureLocked(pg);
          return <button key={i} onClick={() => go(pg)}
            onPointerDown={(e) => { e.currentTarget.style.transform = "scale(0.96)"; }}
            onPointerUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
            onPointerLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
            style={{
              background: `${h.ac}0d`, border: "none", borderRadius: 18,
              padding: "13px 4px 11px", cursor: "pointer",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 7,
              opacity: locked ? 0.55 : 1, position: "relative",
              transition: "transform .18s cubic-bezier(.32,.72,.22,1)",
              WebkitTapHighlightColor: "transparent",
            }}>
            <span style={{ width: 38, height: 38, borderRadius: 13, background: h.dk ? `${h.ac}1f` : "#fff", boxShadow: h.dk ? "none" : "0 2px 8px rgba(244,63,94,0.10)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Ic n={ic} s={17} c={h.a2} />
            </span>
            <span style={{ color: h.a2, fontSize: 10, fontWeight: 600, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>{l}</span>
            {locked && <span style={{ position: "absolute", top: 6, right: 6, fontSize: 9 }}>{"\uD83D\uDD12"}</span>}
          </button>;
        })}
      </div>
    </div>

    {/* ────────────────────────────────────────────────────────────────
        Properties — 2-column grid preview, capped at 4 on the dashboard.
        The full list lives on the dedicated Properties page (route
        "props"); this section is for at-a-glance scanning with photo
        thumbnails. Tap a card → property detail page. Tap "Show all"
        at the bottom → full Properties page.
        ──────────────────────────────────────────────────────────────── */}
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: sp.md }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
        <h3 style={{ color: h.tx, fontSize: 15, fontWeight: 700, margin: 0, letterSpacing: "-0.1px" }}>{t.properties}</h3>
        {props.length > 4 && (
          <span style={{ color: h.ts || h.sc, fontSize: 11, fontWeight: 500 }}>
            Showing 4 of {props.length}
          </span>
        )}
      </div>
      <button
        onClick={() => go("addProp")}
        style={{
          background: h.ac, border: "none", color: "#fff",
          padding: "6px 12px", borderRadius: 8, cursor: "pointer",
          fontSize: 12, fontWeight: 600, flexShrink: 0,
          display: "flex", alignItems: "center", gap: 4,
          transition: "opacity 0.15s",
        }}
        onMouseOver={e => { e.currentTarget.style.opacity = "0.88"; }}
        onMouseOut={e  => { e.currentTarget.style.opacity = "1"; }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1 }}>+</span> {t.addProp}
      </button>
    </div>

    <div className="no-scrollbar" ref={propDrag.ref} {...propDrag.handlers} style={IS_DESKTOP
      ? { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: sp.md, marginBottom: sp.md }
      : { display: "flex", gap: 12, overflowX: "auto", scrollSnapType: "x proximity", WebkitOverflowScrolling: "touch", padding: "2px 2px 10px", marginBottom: sp.md, cursor: "grab" }}>
    {props.slice(0, 4).map(p => {
      const te         = tens.find(x => x.id === p.tid);
      const pLease     = getLeaseForProperty(leases, p.id);
      const pLeaseDays = pLease ? getDaysRemaining(pLease) : null;
      const firstPhoto = p.photos && p.photos[0];
      return (
        <div
          key={p.id}
          onClick={() => { setSelPropId(p.id); go("propDetail"); }}
          style={{
            background: h.cd, borderRadius: 20,
            overflow: "hidden", cursor: "pointer",
            boxShadow: h.sh,
            transition: "transform 0.2s, box-shadow 0.2s",
            display: "flex", flexDirection: "column",
            minWidth: 0,
            ...(IS_DESKTOP ? {} : { width: 232, flexShrink: 0, scrollSnapAlign: "start" }),
          }}
          onMouseOver={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = h.s2 || h.sh; }}
          onMouseOut={e  => { e.currentTarget.style.transform = "none";              e.currentTarget.style.boxShadow = h.sh; }}
        >
          {/* Photo / placeholder banner */}
          <div style={{
            position: "relative",
            aspectRatio: "16/11",
            background: firstPhoto ? "#000" : (h.gl || `linear-gradient(135deg, ${h.ac}20 0%, ${h.ac}05 100%)`),
            display: "flex", alignItems: "center", justifyContent: "center",
            overflow: "hidden",
          }}>
            {firstPhoto ? (
              <img
                src={firstPhoto}
                alt={formatPropAddr(p)}
                loading="lazy"
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            ) : (
              <Ic n="bldg" s={32} c={h.ac} />
            )}
            {/* Vacant / Occupied pill on the photo */}
            <span style={{
              position: "absolute", top: 6, right: 6,
              background: p.occ ? "rgba(22,163,74,0.95)" : "rgba(244,63,94,0.95)",
              color: "#fff", padding: "3px 8px", borderRadius: rad.pill,
              fontSize: 9, fontWeight: 700, letterSpacing: "0.3px",
              textTransform: "uppercase",
              boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
              backdropFilter: "blur(4px)",
            }}>
              {p.occ ? t.occupied : t.vacant}
            </span>
            {/* Lease-expiry warning overlay (within 30 days) */}
            {pLease && pLease.end_date && pLeaseDays !== null && pLeaseDays <= 30 && (
              <span style={{
                position: "absolute", bottom: 6, left: 6,
                background: "rgba(217,119,6,0.95)", color: "#fff",
                padding: "2px 8px", borderRadius: rad.pill,
                fontSize: 9, fontWeight: 700,
                boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
              }}>
                {pLeaseDays}d to expiry
              </span>
            )}
          </div>

          {/* Info block */}
          <div style={{ padding: 10, flex: 1, display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
            <div style={{
              color: h.tx, fontWeight: 600, fontSize: 12, lineHeight: 1.3,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }} title={formatPropAddr(p)}>
              {formatPropAddr(p)}
            </div>

            {/* Property type chip \u2014 shown for every property (defaults
                to Apartment when unset) so the card always states what
                kind of property it is. */}
            <span style={{
              alignSelf: "flex-start",
              background: isCommercial(p.property_type) ? `${h.gn}12` : `${h.ac}12`,
              color: isCommercial(p.property_type) ? h.gn : h.ac,
              padding: "1px 7px", borderRadius: rad.pill,
              fontSize: 9, fontWeight: 600,
            }}>
              {ptLabel(p.property_type || "apartment", t)}
            </span>

            <div style={{ color: h.ts || h.sc, fontSize: 10 }}>
              {p.rooms} {t.rooms} {"\u00B7"} {"\u20B9"}{p.rent >= 1000 ? `${(p.rent / 1000).toFixed(p.rent % 1000 === 0 ? 0 : 1)}k` : p.rent.toLocaleString()}/mo
            </div>

            {/* Tenant chip — its own tap target (stops the card's
                propDetail navigation) routing straight to the tenant
                detail page. Accent-tinted pill so it reads as tappable;
                shows the tenant's DP via the shared Avatar. */}
            {te && (
              <div
                onClick={(e) => { e.stopPropagation(); localStorage.setItem("editTid", te.id); go("tenantDetail"); }}
                title={`View ${te.name}`}
                style={{
                  display: "flex", alignItems: "center", gap: 6, marginTop: 4,
                  background: `${h.ac}12`, border: `1px solid ${h.ac}25`,
                  borderRadius: rad.pill, padding: "3px 8px 3px 3px",
                  cursor: "pointer", transition: "background 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = `${h.ac}1F`; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = `${h.ac}12`; }}
              >
                <Avatar src={te.avatar_url} name={te.name} size={18} fontSize={9} />
                <span style={{
                  color: h.ac, fontSize: 10, fontWeight: 600,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  flex: 1, minWidth: 0,
                }}>
                  {te.name}
                </span>
                {te.vac ? (
                  <span style={{
                    background: `${h.rd}15`, color: h.rd,
                    padding: "1px 5px", borderRadius: rad.pill,
                    fontSize: 8, fontWeight: 700,
                    flexShrink: 0,
                  }}>
                    {t.vacate}
                  </span>
                ) : (
                  <span style={{ color: h.ac, fontSize: 12, fontWeight: 700, flexShrink: 0, lineHeight: 1 }}>{"›"}</span>
                )}
              </div>
            )}
          </div>
        </div>
      );
    })}
    </div>

    {/* Show more → opens the dedicated Properties page (route "props") */}
    {props.length > 4 && (
      <button
        onClick={() => go("props")}
        style={{
          width: "100%", marginBottom: sp.md,
          background: `${h.ac}10`, color: h.ac,
          border: `1px solid ${h.ac}30`,
          padding: "12px 14px", borderRadius: rad.md,
          cursor: "pointer", fontSize: 13, fontWeight: 600,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          transition: "background 0.15s",
        }}
        onMouseOver={e => { e.currentTarget.style.background = `${h.ac}1A`; }}
        onMouseOut={e  => { e.currentTarget.style.background = `${h.ac}10`; }}
      >
        Show all {props.length} properties
        <span style={{ fontSize: 15, fontWeight: 700 }}>{"→"}</span>
      </button>
    )}

    {/* ────────────────────────────────────────────────────────────────
        Tenants — mirrors the Properties section (2-col grid, cap of 4,
        Show all link). Tenants don't have photos so the banner shows
        a colorful avatar circle with the initial; an avatar palette
        cycles through 5 tones to make the grid feel less monotone.
        Tap a card → tenantDetail page.
        ──────────────────────────────────────────────────────────────── */}
    {(() => {
      // Deterministic per-tenant color so the same tenant always gets
      // the same avatar tone across renders.
      const palette = [h.ac, h.gn, h.wn, h.pp || h.ac, h.rd];
      const colorFor = (te) => {
        const sum = String(te.id || te.name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
        return palette[sum % palette.length];
      };
      return <>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: sp.md, marginTop: sp.lg }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
            <h3 style={{ color: h.tx, fontSize: 15, fontWeight: 700, margin: 0, letterSpacing: "-0.1px" }}>
              {t.tenants}
            </h3>
          </div>
          <button
            onClick={() => go("addTenant")}
            style={{
              background: h.ac, border: "none", color: "#fff",
              padding: "6px 12px", borderRadius: 8, cursor: "pointer",
              fontSize: 12, fontWeight: 600, flexShrink: 0,
              display: "flex", alignItems: "center", gap: 4,
              transition: "opacity 0.15s",
            }}
            onMouseOver={e => { e.currentTarget.style.opacity = "0.88"; }}
            onMouseOut={e  => { e.currentTarget.style.opacity = "1"; }}
          >
            <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1 }}>+</span> {t.addTenant || "Add Tenant"}
          </button>
        </div>

        {tens.length === 0 && (
          <div style={{ ...crd(h), textAlign: "center", padding: sp.lg, marginBottom: sp.md, color: h.ts || h.sc, fontSize: 12 }}>
            {t.noTenFound || "No tenants yet"} — tap <b style={{ color: h.ac }}>+ {t.addTenant || "Add Tenant"}</b> to add one.
          </div>
        )}

        {/* iOS-Contacts-style strip: round face, first name, tiny status
            (Assigned / Unassigned / Vacate) with a colored presence dot.
            Swipes horizontally, no visible scrollbar. */}
        {tens.length > 0 && <div className="no-scrollbar" ref={tenDrag.ref} {...tenDrag.handlers} style={{ display: "flex", gap: 16, overflowX: "auto", WebkitOverflowScrolling: "touch", padding: "6px 2px 10px", marginBottom: sp.md, cursor: "grab" }}>
          {tens.map(te => {
            const linkedProp = props.find(x => x.tid === te.id);
            const avatarBg   = colorFor(te);
            const assigned   = !!linkedProp && !te.vac;
            return (
              <div key={te.id} onClick={() => { localStorage.setItem("editTid", te.id); go("tenantDetail"); }}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, cursor: "pointer", flexShrink: 0, width: 66, WebkitTapHighlightColor: "transparent" }}>
                <div style={{ position: "relative" }}>
                  <Avatar src={te.avatar_url} name={te.name} size={56} bg={avatarBg} fontSize={20} />
                  <span style={{ position: "absolute", bottom: 1, right: 1, width: 13, height: 13, borderRadius: "50%", background: te.vac ? h.rd : assigned ? "#22c55e" : h.wn, border: `2.5px solid ${h.bg}` }} />
                </div>
                <div style={{ textAlign: "center", minWidth: 0, width: "100%" }}>
                  <div style={{ color: h.tx, fontSize: 11, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(te.name || "").split(" ")[0]}</div>
                  <div style={{ color: te.vac ? h.rd : assigned ? h.gn : h.wn, fontSize: 9, fontWeight: 600, marginTop: 1 }}>{te.vac ? t.vacate : assigned ? (t.assigned || "Assigned") : (t.unassigned || "Unassigned")}</div>
                </div>
              </div>
            );
          })}
        </div>}

      </>;
    })()}
    {showUpgrade && <UpgradeModal h={h} t={t} used={usage.used} limit={usage.limit} currentPlan={plan.type} onClose={() => setShowUpgrade(false)} onUpgradeSuccess={async () => { if (refreshData) await refreshData(); flash(t.billingSuccess || "Plan Upgraded!"); setShowUpgrade(false); }} />}
  </div>;
}

export default LandlordDash;
